import { z } from 'zod';
import { DEFAULT_SCOPE } from '../components/filters/constants';
import type { ScopeState } from '../components/filters/types';
import { searchFilterKeys } from './filter-search';
import { commaSeparatedArray, isPlainObject } from './search-codec';

const dateRangeSchema = z.object({
  mode: z.enum(['preset', 'custom']),
  preset: z.string(),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
});

export const scopeSearchShape = {
  brand: commaSeparatedArray.optional().catch(undefined),
  platform: commaSeparatedArray.optional().catch(undefined),
  account: commaSeparatedArray.optional().catch(undefined),
  creativeType: commaSeparatedArray.optional().catch(undefined),
  leadingKpi: z.string().optional().catch(undefined),
  range: dateRangeSchema.optional().catch(undefined),
};

const scopeSearchSchema = z.object(scopeSearchShape).catch({});

// Pages that read the shared scope from the URL. Moving between them keeps the brand, platform,
// account, date range and board filters; other pages open with a clean URL.
const SCOPED_ROUTES = new Set<string>([
  '/dashboard',
  '/workflows',
  '/reports/performance',
  '/explore',
  '/elements-explorer',
  '/media',
  '/outcomes',
]);
// Media's board keeps its filters under `filters`; Explore / Elements / Workflows keep theirs as
// top-level catalog keys plus `metric`.
const CARRIED_SEARCH_KEYS = [...Object.keys(scopeSearchShape), 'filters', 'metric'];
// Home and Media keep market, objective and creative type inside `filters`; every other scoped page
// reads them as catalog keys and the scope's creativeType.
const BOARD_FILTER_ROUTES = new Set<string>(['/dashboard', '/media']);
const CATALOG_KEY_BY_BOARD_FILTER = {
  countries: 'country',
  objectives: 'campaignObjective',
  creativeTypes: 'creativeType',
} as const;

export type ScopeSearch = z.infer<typeof scopeSearchSchema>;

function isDefaultRange(range: ScopeState['range']): boolean {
  const fallback = DEFAULT_SCOPE.range;
  return (
    range.mode === fallback.mode &&
    range.preset === fallback.preset &&
    range.from === fallback.from &&
    range.to === fallback.to
  );
}

export function serializeScope(scope: ScopeState): ScopeSearch | undefined {
  const search: ScopeSearch = {};
  if (scope.brand.length) search.brand = scope.brand;
  if (scope.platform.length) search.platform = scope.platform;
  if (scope.account.length) search.account = scope.account;
  if (scope.creativeType.length) search.creativeType = scope.creativeType;
  search.leadingKpi = scope.leadingKpi;
  if (!isDefaultRange(scope.range)) {
    const range: NonNullable<ScopeSearch['range']> = {
      mode: scope.range.mode,
      preset: scope.range.preset,
    };
    if (scope.range.from) range.from = scope.range.from;
    if (scope.range.to) range.to = scope.range.to;
    search.range = range;
  }
  return Object.keys(search).length ? search : undefined;
}

export function scopeFromSearch(search: ScopeSearch | undefined): ScopeState {
  return {
    brand: search?.brand ?? DEFAULT_SCOPE.brand,
    platform: search?.platform ?? DEFAULT_SCOPE.platform,
    account: search?.account ?? DEFAULT_SCOPE.account,
    creativeType: search?.creativeType ?? DEFAULT_SCOPE.creativeType,
    leadingKpi: search?.leadingKpi ?? DEFAULT_SCOPE.leadingKpi,
    range: search?.range
      ? {
          mode: search.range.mode,
          preset: search.range.preset,
          from: search.range.from ?? '',
          to: search.range.to ?? '',
        }
      : DEFAULT_SCOPE.range,
  };
}

export function mergeScopeIntoSearch(previous: object, next: ScopeState): Record<string, unknown> {
  const search: Record<string, unknown> = { ...previous };
  for (const key of Object.keys(scopeSearchShape)) delete search[key];
  return { ...search, ...serializeScope(next) };
}

export function searchHasScope(search: object): boolean {
  const scopeKeys = new Set<string>(Object.keys(scopeSearchShape));
  return Object.entries(search).some(([key, value]) => scopeKeys.has(key) && value !== undefined);
}

/** Moves market, objective and creative type from the shape `from` reads into the shape `to` reads. */
function inDestinationShape(
  from: string,
  to: string,
  search: Record<string, unknown>,
): Record<string, unknown> {
  const fromBoard = BOARD_FILTER_ROUTES.has(from.replace(/\/$/, ''));
  const toBoard = BOARD_FILTER_ROUTES.has(to);
  const out = { ...search };
  const boardFilters: Record<string, unknown> = isPlainObject(search['filters'])
    ? { ...search['filters'] }
    : {};
  for (const [boardKey, catalogKey] of Object.entries(CATALOG_KEY_BY_BOARD_FILTER)) {
    // Only the page being left showed its own shape; the other copy is stale.
    const value = fromBoard ? boardFilters[boardKey] : out[catalogKey];
    delete out[catalogKey];
    delete boardFilters[boardKey];
    if (value === undefined) continue;
    if (toBoard) boardFilters[boardKey] = value;
    else out[catalogKey] = value;
  }
  if (Object.keys(boardFilters).length) out['filters'] = boardFilters;
  else delete out['filters'];
  return out;
}

export function carriedSearch(
  from: string,
  to: string,
  previous: Record<string, unknown>,
): Record<string, unknown> {
  if (!SCOPED_ROUTES.has(to)) return {};
  return inDestinationShape(
    from,
    to,
    Object.fromEntries(
      [...CARRIED_SEARCH_KEYS, ...searchFilterKeys(previous)].flatMap((key) =>
        previous[key] === undefined ? [] : [[key, previous[key]]],
      ),
    ),
  );
}
